import React, { useState, useEffect } from "react";
import {
  Card,
  PageHeader,
  Button,
  StatusBadge,
  ConfirmationModal,
} from "../../../components/common";
import {
  useAppDispatch,
  useAppSelector,
  getTeacherSubmittedArtworksThunk,
  markArtworkThunk,
  setSelectedArtwork,
  getPreviewLinkThunk,
  getDownloadLinkThunk,
} from "../../../redux";
import { showSuccess, showError } from "../../../services";
import { ArtworkSubmission } from "../../../types";
import { extractWorkdriveResourceId } from "../../../utils";
import eyeIcon from "../../../assets/icons/eye.svg";
import downloadIcon from "../../../assets/icons/download.svg";
import editIcon from "../../../assets/icons/edit.svg";
import markIcon from "../../../assets/icons/mark.svg";
import "./TeacherArtworks.css";

const TeacherArtworks: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { teacherArtworks, selectedArtwork, isLoading } = useAppSelector(
    (state) => state.artwork,
  );

  const [feedback, setFeedback] = useState<string>("");
  const [marks, setMarks] = useState<string>("");
  const [downloadTarget, setDownloadTarget] = useState<{
    workdriveUrl?: string;
    fileName?: string;
  } | null>(null);

  useEffect(() => {
    dispatch(getTeacherSubmittedArtworksThunk(user?.id));
  }, [dispatch, user?.id]);

  const handleOpenMarkModal = (item: ArtworkSubmission) => {
    dispatch(setSelectedArtwork(item));
    setFeedback(item.feedback || "");
    setMarks(
      item.marks !== null && item.marks !== undefined ? String(item.marks) : "",
    );
  };

  const handleCloseModal = () => {
    dispatch(setSelectedArtwork(null));
  };

  const handleMarkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedArtwork) return;

    const parsedMarks = Number(marks);
    if (isNaN(parsedMarks) || parsedMarks < 0 || parsedMarks > 100) {
      showError("Please enter a valid mark between 0 and 100.");
      return;
    }

    dispatch(
      markArtworkThunk({
        artwork_id: selectedArtwork.id,
        marks: parsedMarks,
        feedback: feedback.trim(),
      }),
    ).then((res) => {
      if (markArtworkThunk.fulfilled.match(res)) {
        showSuccess("Artwork marked successfully!");
        handleCloseModal();
      }
    });
  };

  const handleViewAttachment = (fileId: string) => {
    if (!fileId) return;
    dispatch(getPreviewLinkThunk(fileId)).then((res) => {
      if (getPreviewLinkThunk.fulfilled.match(res)) {
        const previewUrl = res.payload.previewUrl;
        if (previewUrl) {
          window.open(previewUrl, "_blank");
        }
      }
    });
  };

  const handleDownloadAttachment = (
    workdriveUrl?: string,
    fileName?: string,
  ) => {
    if (!workdriveUrl) return;
    dispatch(getDownloadLinkThunk(workdriveUrl)).then((res) => {
      if (getDownloadLinkThunk.fulfilled.match(res)) {
        const url = window.URL.createObjectURL(res.payload);
        const a = document.createElement("a");
        a.href = url;
        if (fileName) a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card style={{ padding: "24px" }}>
        {teacherArtworks.length === 0 ? (
          <div
            style={{ padding: "32px", textAlign: "center", color: "#8c8594" }}
          >
            No artwork submissions found.
          </div>
        ) : (
          <div className="artworks-table-container">
            <table className="artworks-table">
              <thead>
                <tr>
                  <th>Submission Date</th>
                  <th>Student Name</th>
                  <th>Artwork Name</th>
                  <th>Status</th>
                  <th>Marks</th>
                  <th>Attachments</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {teacherArtworks.map((item) => (
                  <tr key={item.id}>
                    <td>{item.submission_date}</td>
                    <td>
                      <strong>{item.student_name}</strong>
                    </td>
                    <td>{item.assignment_name}</td>
                    <td>
                      <StatusBadge
                        variant={
                          item.status === "Graded" ? "success" : "warning"
                        }
                      >
                        {item.status}
                      </StatusBadge>
                    </td>
                    <td>
                      {item.marks !== null && item.marks !== undefined ? (
                        <strong style={{ color: "#29252d" }}>
                          {item.marks} / 100
                        </strong>
                      ) : (
                        <span style={{ color: "#8c8594" }}>Unmarked</span>
                      )}
                    </td>
                    <td>
                      {(() => {
                        const resourceId = extractWorkdriveResourceId(
                          item.workdrive_url,
                        );
                        if (!resourceId) {
                          return <span style={{ color: "#8c8594" }}>None</span>;
                        }
                        return (
                          <div className="attachments-action-group">
                            <button
                              type="button"
                              className="attachment-action-btn"
                              title="View Attachment"
                              aria-label="View Attachment"
                              onClick={() => handleViewAttachment(resourceId)}
                            >
                              <img
                                src={eyeIcon}
                                alt="View"
                                width={16}
                                height={16}
                              />
                            </button>
                            <button
                              type="button"
                              className="attachment-action-btn"
                              title="Download Attachment"
                              aria-label="Download Attachment"
                              onClick={() =>
                                setDownloadTarget({
                                  workdriveUrl: item.workdrive_url,
                                  fileName: item.assignment_name,
                                })
                              }
                            >
                              <img
                                src={downloadIcon}
                                alt="Download"
                                width={16}
                                height={16}
                              />
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <Button
                        variant={
                          item.status === "Graded" ? "outline" : "primary"
                        }
                        size="small"
                        onClick={() => handleOpenMarkModal(item)}
                        title={
                          item.status === "Graded"
                            ? "Edit Grade & Feedback"
                            : "Mark Artwork"
                        }
                        aria-label={
                          item.status === "Graded"
                            ? "Edit Grade & Feedback"
                            : "Mark Artwork"
                        }
                        className="action-icon-button"
                      >
                        <img
                          src={item.status === "Graded" ? editIcon : markIcon}
                          alt={
                            item.status === "Graded"
                              ? "Edit Grade"
                              : "Mark Artwork"
                          }
                          width={16}
                          height={16}
                        />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Mark Artwork Modal */}
      {selectedArtwork && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Mark Artwork</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleMarkSubmit} className="modal-form">
              <div className="modal-body mark-modal-body">
                <div className="mark-student-summary">
                  <div>
                    <span className="mark-summary-label">Student: </span>
                    <span className="mark-summary-val">
                      {selectedArtwork.student_name}
                    </span>
                  </div>
                  <div>
                    <span className="mark-summary-label">Artwork: </span>
                    <span className="mark-summary-val">
                      {selectedArtwork.assignment_name}
                    </span>
                  </div>
                  <div>
                    <span className="mark-summary-label">Submitted On: </span>
                    <span>{selectedArtwork.submission_date}</span>
                  </div>
                </div>

                <div className="content-field">
                  <label htmlFor="marksInput">Marks (0 - 100)</label>
                  <input
                    type="number"
                    id="marksInput"
                    min="0"
                    max="100"
                    placeholder="Enter marks"
                    value={marks}
                    onChange={(e) => setMarks(e.target.value)}
                    required
                  />
                </div>

                <div className="content-field">
                  <label htmlFor="feedbackInput">Feedback Comment</label>
                  <textarea
                    id="feedbackInput"
                    placeholder="Enter detailed feedback comment for the student..."
                    rows={4}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="modal-footer">
                <Button
                  type="button"
                  variant="outline"
                  size="medium"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" size="medium">
                  Submit Mark & Feedback
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attachment Download Confirmation Modal */}
      <ConfirmationModal
        isOpen={Boolean(downloadTarget)}
        title="Confirm Download"
        message={
          downloadTarget?.fileName
            ? `Are you sure you want to download "${downloadTarget.fileName}"?`
            : "Are you sure you want to download this student attachment?"
        }
        confirmText="Download"
        cancelText="Cancel"
        confirmVariant="primary"
        onConfirm={() => {
          if (downloadTarget?.workdriveUrl) {
            handleDownloadAttachment(
              downloadTarget.workdriveUrl,
              downloadTarget.fileName,
            );
          }
          setDownloadTarget(null);
        }}
        onCancel={() => setDownloadTarget(null)}
      />
    </div>
  );
};

export default TeacherArtworks;
